/**
 * Realtime Hub client-side de la Capa Operacional AI-First.
 *
 * Implementa la interfaz {@link Realtime} de `../api/contracts` para la
 * variante **demo** (client-side, R17.1/R17.5). Es la columna vertebral
 * de streaming y live updates (R7): un **simulador de stream** sobre
 * `EventTarget` (y `BroadcastChannel` cuando está disponible para
 * propagación cross-tab) **alimentado por el Event_Store**.
 *
 * Responsabilidades (R7):
 *   - `subscribe(filtro)` valida el filtro; si es inválido **rechaza sin
 *     abrir canal** (R7.3) y, si es válido, abre un canal que entrega
 *     **únicamente** los Eventos_Operacionales cuyo tipo o entidad
 *     pertenece al filtro (R7.2), en **orden temporal no decreciente**
 *     (R7.4);
 *   - `resume(canal, last_seq)` reanuda la entrega: dentro de la ventana
 *     de **60 s** sin pérdida ni duplicado (R7.7); superada la ventana,
 *     ejecuta una **re-sincronización** que reconstruye el estado de las
 *     entidades suscritas por replay desde el Event_Store antes de
 *     reanudar la entrega incremental (R7.8);
 *   - la ubicación de una Cuadrilla se transmite **solo a suscriptores
 *     autorizados** según la matriz RBAC (R7.6);
 *   - notifica Alertas y sus cambios de estado a los suscriptores
 *     afectados (R7.5).
 *
 * Diseño orientado a PBT (Properties 43 y 44): toda la lógica de
 * validación de filtro, coincidencia, orden temporal, corte de
 * reanudación (resume vs re-sync) y restricción RBAC vive en **funciones
 * puras** exportadas, sin tocar `EventTarget`/IndexedDB, de modo que los
 * tests (tareas 5.3 y 5.4) las ejerciten directamente. La clase
 * {@link RealtimeHubLocal} solo **cablea** esas funciones al stream.
 */

import type {
  CanalRealtime,
  EntidadRef,
  EventStore,
  EventoOperacional,
  FiltroRealtime,
  Id,
  Realtime,
  Result,
  Rol,
  TipoEntidad,
} from "../api/contracts";
import { provideDemo, runtime } from "../api/runtime";

// ---------------------------------------------------------------------------
// Configuración
// ---------------------------------------------------------------------------

/** Ventana de reconexión sin pérdida/duplicado: 60 s (R7.7, R7.8). */
export const VENTANA_REANUDACION_MS = 60_000;

/**
 * Tipos de entidad reconocidos como nodos del Grafo_Entidades. Un filtro
 * que referencia un tipo fuera de este conjunto es inválido (R7.3).
 * Coincide con la unión {@link TipoEntidad} de `contracts.ts`.
 */
export const TIPOS_ENTIDAD_CONOCIDOS: ReadonlySet<TipoEntidad> = new Set<TipoEntidad>([
  "Cliente",
  "Medidor",
  "DMA",
  "Gateway",
  "Anomalia",
  "Alerta",
  "Orden_Inspeccion",
  "Caso",
  "Valvula",
  "PRV",
  "Cuadrilla",
  "Presion",
  "Insight",
]);

/**
 * Tipos de Evento_Operacional reconocidos por defecto. Un filtro que
 * referencia un `tipo` fuera de este conjunto se considera inválido
 * (R7.3). Es **configurable** vía las opciones de validación para no
 * acoplar la Capa a un catálogo cerrado.
 */
export const TIPOS_EVENTO_CONOCIDOS: ReadonlySet<string> = new Set<string>([
  "anomalia_detectada",
  "alerta_generada",
  "alerta_cambio_estado",
  "lectura_ingerida",
  "presion_registrada",
  "caso_creado",
  "caso_actualizado",
  "caso_consolidado",
  "orden_creada",
  "orden_actualizada",
  "valvula_abierta",
  "valvula_cerrada",
  "prv_setpoint_cambiado",
  "cuadrilla_ubicacion",
  "cuadrilla_estado",
  "evidencia_capturada",
  "accion_ejecutada",
]);

/**
 * Tipos de evento que representan una actualización de **ubicación de
 * Cuadrilla** (R7.6). Su entrega está restringida por RBAC.
 */
export const TIPOS_UBICACION_CUADRILLA: ReadonlySet<string> = new Set<string>([
  "cuadrilla_ubicacion",
]);

/**
 * Roles autorizados a recibir la ubicación en vivo de una Cuadrilla
 * según la matriz RBAC de la fundación (R7.6). El Despachador coordina
 * cuadrillas; el Supervisor las gestiona; el Operador monitorea la
 * operación. Roles fuera de este conjunto **no** reciben la ubicación.
 */
export const ROLES_UBICACION_CUADRILLA: ReadonlySet<Rol> = new Set<Rol>([
  "Despachador",
  "Supervisor",
  "Operador",
]);

// ---------------------------------------------------------------------------
// Tipos auxiliares
// ---------------------------------------------------------------------------

/**
 * Evento del log global del Hub con su **secuencia global** monótona
 * (1-based, en orden de inserción). `seq_global` es el cursor de
 * reanudación: garantiza entrega exactly-once sin pérdida ni duplicado
 * (R7.7) y desempata el orden temporal de forma determinista (R7.4).
 */
export interface EventoConSeq {
  evento: EventoOperacional;
  seq_global: number;
}

/** Opciones de validación de un {@link FiltroRealtime} (R7.3). */
export interface OpcionesValidacionFiltro {
  /** Conjunto de tipos de evento reconocidos (por defecto {@link TIPOS_EVENTO_CONOCIDOS}). */
  tiposConocidos?: ReadonlySet<string>;
  /** Conjunto de tipos de entidad reconocidos (por defecto {@link TIPOS_ENTIDAD_CONOCIDOS}). */
  entidadesConocidas?: ReadonlySet<TipoEntidad>;
}

function err<T>(error: string, degraded = false): Result<T> {
  return { ok: false, error, degraded };
}

function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

function entityKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

// ---------------------------------------------------------------------------
// Validación de filtro — R7.3 (función pura)
// ---------------------------------------------------------------------------

/**
 * Valida un {@link FiltroRealtime} (R7.3). Un filtro es **inválido** si:
 *   - es nulo o no es un objeto (mal formado);
 *   - no especifica **ni** `tipos` **ni** `entidades` (no hay nada que
 *     suscribir);
 *   - declara un array vacío o con entradas mal formadas;
 *   - referencia un **tipo de evento** no reconocido;
 *   - referencia una **entidad** cuyo `tipo` no es reconocido o cuyo `id`
 *     está vacío.
 *
 * Devuelve un {@link Result} con el filtro normalizado si es válido, o un
 * error descriptivo si no lo es (el llamador **no** debe abrir el canal).
 */
export function validarFiltro(
  filtro: FiltroRealtime | null | undefined,
  opts?: OpcionesValidacionFiltro,
): Result<FiltroRealtime> {
  if (filtro === null || typeof filtro !== "object") {
    return err("Filtro inválido: la suscripción requiere un filtro bien formado (R7.3).");
  }

  const tiposConocidos = opts?.tiposConocidos ?? TIPOS_EVENTO_CONOCIDOS;
  const entidadesConocidas = opts?.entidadesConocidas ?? TIPOS_ENTIDAD_CONOCIDOS;

  const tieneTipos = filtro.tipos !== undefined;
  const tieneEntidades = filtro.entidades !== undefined;

  if (!tieneTipos && !tieneEntidades) {
    return err(
      "Filtro inválido: debe especificar al menos un tipo de evento o una entidad a suscribir (R7.3).",
    );
  }

  // Validación de tipos de evento.
  if (tieneTipos) {
    const tipos = filtro.tipos;
    if (!Array.isArray(tipos) || tipos.length === 0) {
      return err("Filtro inválido: 'tipos' debe ser un array no vacío (R7.3).");
    }
    for (const t of tipos) {
      if (typeof t !== "string" || t.length === 0) {
        return err("Filtro inválido: 'tipos' contiene un tipo de evento mal formado (R7.3).");
      }
      if (!tiposConocidos.has(t)) {
        return err(`Filtro inválido: tipo de evento no reconocido '${t}' (R7.3).`);
      }
    }
  }

  // Validación de entidades.
  if (tieneEntidades) {
    const entidades = filtro.entidades;
    if (!Array.isArray(entidades) || entidades.length === 0) {
      return err("Filtro inválido: 'entidades' debe ser un array no vacío (R7.3).");
    }
    for (const ref of entidades) {
      if (ref === null || typeof ref !== "object") {
        return err("Filtro inválido: 'entidades' contiene una referencia mal formada (R7.3).");
      }
      if (typeof ref.id !== "string" || ref.id.length === 0) {
        return err("Filtro inválido: una entidad del filtro carece de 'id' válido (R7.3).");
      }
      if (!entidadesConocidas.has(ref.tipo)) {
        return err(`Filtro inválido: tipo de entidad no reconocido '${String(ref.tipo)}' (R7.3).`);
      }
    }
  }

  return ok(filtro);
}

// ---------------------------------------------------------------------------
// Coincidencia con el filtro — R7.2 (función pura)
// ---------------------------------------------------------------------------

/**
 * Indica si un Evento_Operacional pertenece al conjunto suscrito por el
 * filtro: su **tipo** está en `filtro.tipos` **o** su **entidad** está en
 * `filtro.entidades` (R7.2). Un filtro sin `tipos` ni `entidades` no
 * coincide con nada (debería haberse rechazado en {@link validarFiltro}).
 */
export function eventoCoincideFiltro(evento: EventoOperacional, filtro: FiltroRealtime): boolean {
  const porTipo = filtro.tipos?.includes(evento.tipo) ?? false;
  if (porTipo) return true;

  if (filtro.entidades && filtro.entidades.length > 0) {
    const clave = entityKey(evento.entidad_ref);
    return filtro.entidades.some((ref) => entityKey(ref) === clave);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Orden temporal no decreciente — R7.4 (función pura)
// ---------------------------------------------------------------------------

/**
 * Compara dos eventos por **orden temporal no decreciente** (R7.4):
 * primero por `ts` (ISO-8601 UTC), desempatando de forma determinista por
 * `seq_global` ascendente para preservar estabilidad.
 */
function compararEntrega(a: EventoConSeq, b: EventoConSeq): number {
  const ta = Date.parse(a.evento.ts);
  const tb = Date.parse(b.evento.ts);
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  if (a.evento.ts !== b.evento.ts) return a.evento.ts < b.evento.ts ? -1 : 1;
  return a.seq_global - b.seq_global;
}

/**
 * Ordena una lista de eventos en orden temporal no decreciente (R7.4) sin
 * mutar la entrada. El desempate por `seq_global` lo hace determinista.
 */
export function ordenarEntrega(eventos: EventoConSeq[]): EventoConSeq[] {
  return [...eventos].sort(compararEntrega);
}

// ---------------------------------------------------------------------------
// Corte de reanudación — R7.7 (función pura)
// ---------------------------------------------------------------------------

/**
 * Devuelve los eventos del log con `seq_global > last_seq`, es decir, los
 * **aún no entregados** al suscriptor. Como `seq_global` es único y
 * estrictamente creciente, el resultado no pierde ni duplica eventos
 * (exactly-once, R7.7).
 */
export function eventosTrasSeq(log: EventoConSeq[], last_seq: number): EventoConSeq[] {
  return log.filter((e) => e.seq_global > last_seq);
}

// ---------------------------------------------------------------------------
// RBAC para ubicación de Cuadrilla — R7.6 (funciones puras)
// ---------------------------------------------------------------------------

/**
 * Indica si un evento representa una actualización de **ubicación de
 * Cuadrilla** (R7.6): su entidad es de tipo `Cuadrilla` y su tipo está en
 * {@link TIPOS_UBICACION_CUADRILLA}.
 */
export function esEventoUbicacionCuadrilla(evento: EventoOperacional): boolean {
  return evento.entidad_ref.tipo === "Cuadrilla" && TIPOS_UBICACION_CUADRILLA.has(evento.tipo);
}

/**
 * Indica si un rol está **autorizado** a recibir la ubicación en vivo de
 * una Cuadrilla según la matriz RBAC (R7.6). Un rol nulo o desconocido no
 * está autorizado.
 */
export function puedeRecibirUbicacionCuadrilla(
  rol: Rol | string | null | undefined,
  autorizados: ReadonlySet<Rol> = ROLES_UBICACION_CUADRILLA,
): boolean {
  if (!rol) return false;
  return autorizados.has(rol as Rol);
}

/**
 * Filtra una lista de eventos aplicando la restricción RBAC de ubicación
 * de Cuadrilla (R7.6): si el rol **no** está autorizado, se eliminan los
 * eventos de ubicación de Cuadrilla; el resto se preserva. No muta la
 * entrada.
 */
export function aplicarRBAC(
  eventos: EventoConSeq[],
  rol: Rol | string | null | undefined,
  autorizados: ReadonlySet<Rol> = ROLES_UBICACION_CUADRILLA,
): EventoConSeq[] {
  if (puedeRecibirUbicacionCuadrilla(rol, autorizados)) return [...eventos];
  return eventos.filter((e) => !esEventoUbicacionCuadrilla(e.evento));
}

// ---------------------------------------------------------------------------
// Entrega completa para un suscriptor — composición pura (R7.2, R7.4, R7.6, R7.7)
// ---------------------------------------------------------------------------

/** Parámetros de la entrega a un suscriptor. */
export interface OpcionesEntrega {
  filtro: FiltroRealtime;
  /** Solo se entregan eventos con `seq_global > last_seq` (R7.7). Default 0 (todos). */
  last_seq?: number;
  /** Rol del suscriptor para la restricción RBAC (R7.6). */
  rol?: Rol | string | null;
  /** Conjunto de roles autorizados a ver ubicación de Cuadrilla (R7.6). */
  rolesUbicacion?: ReadonlySet<Rol>;
}

/**
 * Calcula, de forma **pura y determinista**, el conjunto ordenado de
 * eventos a entregar a un suscriptor a partir del log global (R7.2, R7.4,
 * R7.6, R7.7):
 *
 *   1. corta por `last_seq` (exactly-once en reanudación, R7.7);
 *   2. filtra por el filtro suscrito (R7.2);
 *   3. aplica la restricción RBAC de ubicación de Cuadrilla (R7.6);
 *   4. ordena en orden temporal no decreciente (R7.4).
 */
export function entregaParaSuscriptor(
  log: EventoConSeq[],
  opts: OpcionesEntrega,
): EventoConSeq[] {
  const last = opts.last_seq ?? 0;
  const tras = eventosTrasSeq(log, last);
  const filtrados = tras.filter((e) => eventoCoincideFiltro(e.evento, opts.filtro));
  const rbac = aplicarRBAC(filtrados, opts.rol, opts.rolesUbicacion);
  return ordenarEntrega(rbac);
}

// ---------------------------------------------------------------------------
// Decisión resume vs re-sync — R7.7/R7.8 (función pura)
// ---------------------------------------------------------------------------

/** Modo de reanudación de un canal tras una interrupción (R7.7, R7.8). */
export type ModoReanudacion = "resume" | "resync";

/**
 * Decide el modo de reanudación según el tiempo transcurrido desde la
 * desconexión (R7.7, R7.8): dentro de la ventana (≤60 s) se **reanuda**
 * incrementalmente sin pérdida/duplicado; superada la ventana se
 * **re-sincroniza** por replay desde el Event_Store.
 */
export function modoReanudacion(
  desconectadoDesdeMs: number,
  ahoraMs: number,
  ventanaMs: number = VENTANA_REANUDACION_MS,
): ModoReanudacion {
  return ahoraMs - desconectadoDesdeMs <= ventanaMs ? "resume" : "resync";
}

// ---------------------------------------------------------------------------
// RealtimeHubLocal — simulador de stream sobre EventTarget/BroadcastChannel
// ---------------------------------------------------------------------------

/** Callback de entrega de un evento a un suscriptor. */
export type SuscriptorHandler = (evento: EventoOperacional, seq_global: number) => void;

/** Callback de re-sincronización: recibe el estado reconstruido por replay (R7.8). */
export type ResyncHandler = (
  estados: Array<{ entidad_ref: EntidadRef; estado: Record<string, unknown> }>,
) => void;

/** Estado interno de un canal abierto. */
interface CanalState {
  canal: CanalRealtime;
  rol: Rol | string | null;
  handler?: SuscriptorHandler;
  onResync?: ResyncHandler;
  /** Instante de la última actividad/desconexión (para la ventana de 60 s). */
  desconectadoDesdeMs: number | null;
}

/** Opciones de construcción del {@link RealtimeHubLocal}. */
export interface RealtimeHubLocalOptions {
  /** Event_Store que alimenta el stream (por defecto el del runtime). */
  eventStore?: EventStore;
  /** Rol activo del usuario para la restricción RBAC (R7.6). */
  rolActual?: () => Rol | string | null;
  /** Nombre del `BroadcastChannel` para propagación cross-tab. */
  broadcastChannelName?: string;
  /** Ventana de reanudación en ms (R7.7). */
  ventanaMs?: number;
  /** Opciones de validación de filtro (R7.3). */
  validacion?: OpcionesValidacionFiltro;
  /** Reloj inyectable (test). */
  ahora?: () => number;
}

/**
 * Simulador de stream realtime client-side. Mantiene un **log global**
 * ordenado por `seq_global` (orden de inserción), alimentado desde el
 * Event_Store, y hace fan-out a los suscriptores vía `EventTarget` (y
 * `BroadcastChannel` cuando existe, para cross-tab). Toda la política de
 * entrega delega en las funciones puras de este módulo.
 */
export class RealtimeHubLocal implements Realtime {
  private readonly bus = new EventTarget();
  private readonly bc: BroadcastChannel | null;
  private readonly log: EventoConSeq[] = [];
  private readonly canales = new Map<Id, CanalState>();
  private readonly eventStore: EventStore;
  private readonly rolActual: () => Rol | string | null;
  private readonly ventanaMs: number;
  private readonly validacion?: OpcionesValidacionFiltro;
  private readonly ahora: () => number;
  private hidratado = false;

  constructor(opts?: RealtimeHubLocalOptions) {
    this.eventStore = opts?.eventStore ?? runtime.eventStore();
    this.rolActual = opts?.rolActual ?? (() => null);
    this.ventanaMs = opts?.ventanaMs ?? VENTANA_REANUDACION_MS;
    this.validacion = opts?.validacion;
    this.ahora = opts?.ahora ?? (() => Date.now());
    this.bc = this.crearBroadcastChannel(opts?.broadcastChannelName);
  }

  private crearBroadcastChannel(nombre?: string): BroadcastChannel | null {
    if (!nombre) return null;
    try {
      if (typeof BroadcastChannel === "undefined") return null;
      const bc = new BroadcastChannel(nombre);
      bc.onmessage = (msg: MessageEvent) => {
        const data = msg.data as { evento?: EventoOperacional } | null;
        if (data?.evento) this.ingestarLocal(data.evento);
      };
      return bc;
    } catch {
      return null;
    }
  }

  /**
   * Hidrata el log global desde el Event_Store (backfill). Idempotente:
   * solo lee la primera vez. Base de la entrega y de la re-sync (R7.7/8).
   */
  async hydrate(): Promise<void> {
    if (this.hidratado) return;
    this.hidratado = true;
    for await (const evento of this.eventStore.streamFrom(1)) {
      this.anexar(evento);
    }
  }

  /** Anexa un evento al log global asignándole `seq_global` monótona. */
  private anexar(evento: EventoOperacional): EventoConSeq {
    const conSeq: EventoConSeq = { evento, seq_global: this.log.length + 1 };
    this.log.push(conSeq);
    return conSeq;
  }

  /**
   * Inyecta un Evento_Operacional registrado en el Event_Store al stream:
   * lo anexa al log y hace fan-out local. Es el punto de alimentación del
   * simulador (R7.4). También lo propaga por `BroadcastChannel` si existe.
   */
  emit(evento: EventoOperacional): void {
    const conSeq = this.ingestarLocal(evento);
    if (this.bc) {
      try {
        this.bc.postMessage({ evento });
      } catch {
        /* canal cerrado: degradación silenciosa */
      }
    }
    void conSeq;
  }

  /** Anexa y notifica localmente sin re-propagar por BroadcastChannel. */
  private ingestarLocal(evento: EventoOperacional): EventoConSeq {
    const conSeq = this.anexar(evento);
    this.bus.dispatchEvent(new CustomEvent("evento", { detail: conSeq }));
    return conSeq;
  }

  /**
   * Abre una Suscripcion_Realtime (R7.1). Valida el filtro; si es inválido
   * **rechaza sin abrir canal** (R7.3). El canal devuelto entrega solo lo
   * suscrito (R7.2) en orden temporal no decreciente (R7.4).
   */
  async subscribe(filtro: FiltroRealtime): Promise<Result<CanalRealtime>> {
    const validacion = validarFiltro(filtro, this.validacion);
    if (!validacion.ok) {
      // R7.3: filtro inválido → no se abre canal, se informa el error.
      return validacion as Result<CanalRealtime>;
    }
    await this.hydrate();

    const canal_id = this.generarId();
    const canal: CanalRealtime = { canal_id, filtro: validacion.value, last_seq: 0 };
    this.canales.set(canal_id, {
      canal,
      rol: this.rolActual(),
      desconectadoDesdeMs: null,
    });
    return ok(canal);
  }

  /**
   * Registra el handler de entrega de un canal abierto y comienza a
   * recibir eventos en vivo (R7.2, R7.4, R7.6). Devuelve una función para
   * desuscribirse (marca el canal como desconectado para la ventana de
   * reanudación). Entrega primero el backlog pendiente del log.
   */
  listen(canal_id: Id, handler: SuscriptorHandler, onResync?: ResyncHandler): () => void {
    const estado = this.canales.get(canal_id);
    if (!estado) {
      // Canal desconocido: no-op seguro.
      return () => {};
    }
    estado.handler = handler;
    estado.onResync = onResync;
    estado.desconectadoDesdeMs = null;

    // Entrega el backlog acumulado (eventos ya en el log) respetando
    // filtro, RBAC y orden temporal (R7.2, R7.4, R7.6).
    const backlog = entregaParaSuscriptor(this.log, {
      filtro: estado.canal.filtro,
      last_seq: estado.canal.last_seq,
      rol: estado.rol,
    });
    for (const e of backlog) {
      estado.canal.last_seq = Math.max(estado.canal.last_seq, e.seq_global);
      handler(e.evento, e.seq_global);
    }

    // Fan-out de eventos en vivo.
    const listener = (ev: Event): void => {
      const { evento, seq_global } = (ev as CustomEvent<EventoConSeq>).detail;
      const entrega = entregaParaSuscriptor([{ evento, seq_global }], {
        filtro: estado.canal.filtro,
        last_seq: estado.canal.last_seq,
        rol: estado.rol,
      });
      for (const e of entrega) {
        estado.canal.last_seq = Math.max(estado.canal.last_seq, e.seq_global);
        handler(e.evento, e.seq_global);
      }
    };
    this.bus.addEventListener("evento", listener);

    return () => {
      this.bus.removeEventListener("evento", listener);
      const s = this.canales.get(canal_id);
      if (s) s.desconectadoDesdeMs = this.ahora();
    };
  }

  /**
   * Reanuda la entrega de un canal tras una interrupción (R7.7, R7.8):
   *
   *   - **≤60 s** (R7.7): reenvía los Eventos_Operacionales con
   *     `seq_global > last_seq` sin pérdida ni duplicado, en orden
   *     temporal no decreciente;
   *   - **>60 s** (R7.8): ejecuta una **re-sincronización** que reconstruye
   *     el estado de las entidades suscritas por replay desde el
   *     Event_Store y luego reanuda la entrega incremental.
   */
  async resume(canal_id: Id, last_seq: number): Promise<Result<void>> {
    const estado = this.canales.get(canal_id);
    if (!estado) {
      return err("Canal desconocido: no se puede reanudar una suscripción inexistente.");
    }
    await this.hydrate();

    const desconectado = estado.desconectadoDesdeMs ?? this.ahora();
    const modo = modoReanudacion(desconectado, this.ahora(), this.ventanaMs);

    if (modo === "resync") {
      // R7.8: reconstruir el estado de las entidades suscritas por replay.
      const refs = this.entidadesSuscritas(estado.canal.filtro);
      const estados: Array<{ entidad_ref: EntidadRef; estado: Record<string, unknown> }> = [];
      const hasta = new Date(this.ahora()).toISOString();
      for (const ref of refs) {
        const res = await this.eventStore.replay(ref, hasta);
        if (res.ok) estados.push({ entidad_ref: ref, estado: res.value.estado });
      }
      estado.onResync?.(estados);
    }

    // En ambos modos se reanuda la entrega incremental desde last_seq sin
    // pérdida ni duplicado (R7.7), respetando filtro, RBAC y orden (R7.2/4/6).
    estado.canal.last_seq = last_seq;
    estado.desconectadoDesdeMs = null;
    const pendientes = entregaParaSuscriptor(this.log, {
      filtro: estado.canal.filtro,
      last_seq,
      rol: estado.rol,
    });
    for (const e of pendientes) {
      estado.canal.last_seq = Math.max(estado.canal.last_seq, e.seq_global);
      estado.handler?.(e.evento, e.seq_global);
    }
    return ok(undefined);
  }

  /** Entidades referenciadas por un filtro (para la re-sync, R7.8). */
  private entidadesSuscritas(filtro: FiltroRealtime): EntidadRef[] {
    if (filtro.entidades && filtro.entidades.length > 0) return [...filtro.entidades];
    // Si el filtro es por tipos, reconstruye las entidades presentes en el
    // log cuyo tipo de evento pertenece al filtro.
    const vistas = new Map<string, EntidadRef>();
    for (const { evento } of this.log) {
      if (filtro.tipos?.includes(evento.tipo)) {
        vistas.set(entityKey(evento.entidad_ref), evento.entidad_ref);
      }
    }
    return [...vistas.values()];
  }

  /** Cierra el Hub y libera el BroadcastChannel. */
  close(): void {
    try {
      this.bc?.close();
    } catch {
      /* ignore */
    }
    this.canales.clear();
  }

  private generarId(): Id {
    try {
      const c = (globalThis as { crypto?: Crypto }).crypto;
      if (c && typeof c.randomUUID === "function") return c.randomUUID();
    } catch {
      /* fallback */
    }
    return `canal-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
  }
}

// ---------------------------------------------------------------------------
// Registro en el runtime selector (R17.1, R17.2, R17.5)
// ---------------------------------------------------------------------------

let singleton: RealtimeHubLocal | null = null;

/** Devuelve (creando perezosamente) el Hub demo singleton de la sesión. */
export function getRealtimeHub(): RealtimeHubLocal {
  if (!singleton) singleton = new RealtimeHubLocal();
  return singleton;
}

let registrado = false;

/**
 * Registra (una sola vez) la implementación DEMO del Realtime Hub en el
 * runtime selector, sin tocar las vistas ni el selector (R17.1, R17.5).
 * Idempotente y seguro de llamar múltiples veces.
 */
export function ensureRealtimeProvider(): void {
  if (registrado) return;
  registrado = true;
  provideDemo("realtime", () => getRealtimeHub());
}

// Auto-registro al importar el módulo: cualquier consumidor (p. ej. el
// hook `useRealtime` de la tarea 5.2/5.5) que importe este módulo deja el
// proveedor DEMO disponible en el runtime selector.
ensureRealtimeProvider();
